/**
 * Client telemetry configuration.
 *
 * There is no Mixpanel token here any more. All Mixpanel calls are made by the
 * backend; the browser only posts a small allowlisted set of events to our own
 * API, which forwards them. See `src/modules/telemetry` in the backend repo.
 */

const readEnv = (key: string): string | undefined => {
    const value = import.meta.env[key as keyof ImportMetaEnv];
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
};

export const ANALYTICS_ENABLED = readEnv('VITE_ENABLE_ANALYTICS') === 'true';

export const ANALYTICS_DEBUG = readEnv('VITE_ENABLE_DEBUG') === 'true';

/** Same base URL the rest of the app uses, so beacons ride the existing origin. */
export const API_BASE_URL = readEnv('VITE_API_BASE_URL') ?? '/api/v1';

/** Endpoint that forwards client-only events to Mixpanel server-side. */
export const TELEMETRY_ENDPOINT = `${API_BASE_URL.replace(/\/$/, '')}/telemetry/events`;

/**
 * Stable per-browser id so anonymous page views on the public pages stitch onto
 * the user's profile once they sign in. Not a credential — the backend replaces
 * it with the authenticated user id whenever a valid token is present.
 */
const DEVICE_ID_KEY = 'yc-device-id';

export const getDeviceId = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;

    try {
        const existing = window.localStorage.getItem(DEVICE_ID_KEY);
        if (existing) return existing;

        const generated =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
                ? crypto.randomUUID()
                : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        window.localStorage.setItem(DEVICE_ID_KEY, generated);
        return generated;
    } catch {
        // Private browsing / storage disabled — events still send, just anonymous.
        return undefined;
    }
};
